import { useState } from 'react';

import { AvatarUnits, Nullable } from 'chaika-frontend-types/frontend-cms';

import { assertIsDefined } from '@utils/assertIsDefined';
import { loadAvatar } from '../utils/loadAvatar';

const defaultAvatarUnits: AvatarUnits = { offsetX: 0, offsetY: 0, scale: 1 };

interface UseAvatarParams {
    userId?: string;
    url?: string;
    avatarUnits?: AvatarUnits;
    onUpdate?: (url: string) => void;
    onAvatarUnitsSave?: (avatarUnits: AvatarUnits) => void;
}

interface UseAvatarResult {
    url?: string;
    avatarUnits: AvatarUnits;
    isEditingAvatar: boolean;
    onChangeClick?: (files: File[]) => void;
    startAvatarEditing(): void;
    changeAvatarUnits(units: Partial<AvatarUnits>): void;
    cancelAvatarEditing(): void;
    endAvatarEditing(): void;
}

export function useAvatar({
    userId,
    url: urlProp,
    avatarUnits: avatarUnitsProp,
    onUpdate,
    onAvatarUnitsSave,
}: UseAvatarParams): UseAvatarResult {
    const [loadedUrl, setLoadedUrl] = useState<Nullable<string>>(null);
    const [isEditingAvatar, setIsEditingAvatar] = useState(false);
    const [avatarUnits, setAvatarUnits] = useState<AvatarUnits>(() => avatarUnitsProp || defaultAvatarUnits);

    const url = loadedUrl || urlProp;

    const startAvatarEditing = () => setIsEditingAvatar(true);

    const changeAvatarUnits = (units: Partial<AvatarUnits>) => {
        setAvatarUnits(oldUnits => ({ ...oldUnits, ...units }));
    };

    const cancelAvatarEditing = () => {
        setIsEditingAvatar(false);
        setAvatarUnits(avatarUnitsProp || defaultAvatarUnits);
    };

    const endAvatarEditing = () => {
        assertIsDefined(onAvatarUnitsSave, 'Avatar is editable only with an onAvatarUnitsSave handler');
        setIsEditingAvatar(false);
        onAvatarUnitsSave(avatarUnits);
    };

    const handleEditClick = async ([file]: File[]) => {
        assertIsDefined(onUpdate, 'Avatar change is enabled only with an onUpdate handler');
        const loadedAvatarUrl = await loadAvatar(file, userId);
        setLoadedUrl(loadedAvatarUrl);
        onUpdate(loadedAvatarUrl);
        startAvatarEditing();
    };

    const onChangeClick = onUpdate ? handleEditClick : undefined;

    return {
        url,
        avatarUnits,
        isEditingAvatar,
        onChangeClick,
        startAvatarEditing,
        changeAvatarUnits,
        cancelAvatarEditing,
        endAvatarEditing,
    };
}
